import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import {
  Building2,
  Calendar,
  Users,
  ShieldCheck,
  Zap,
  Clock,
  ArrowRight,
  Stethoscope,
  Heart
} from 'lucide-react';

const HospitalDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpInputs, setOtpInputs] = useState({});
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/hospital');
      setData(response.data.data);
    } catch (error) {
      toast.error('Failed to load facility analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (surgeryId, otpId, purpose) => {
    const code = otpInputs[surgeryId];
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);
    try {
      const endpoint = purpose === 'surgery_start' || purpose === 'surgery_verification' ? `/surgeries/${surgeryId}/start` : `/surgeries/${surgeryId}/complete`;
      const response = await api.post(endpoint, { otpCode: code, otpId });
      if (response.data.success) {
        toast.success(response.data.message || 'OTP Verified Successfully');
        fetchDashboardData();
        setOtpInputs(prev => ({ ...prev, [surgeryId]: '' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 space-y-10 animate-fadeIn bg-[#F8FAFC] min-h-screen">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex items-center gap-6">
            <img src={logo} alt="Loading..." className="h-20 w-auto animate-pulse opacity-50 grayscale" />
            <div className="space-y-3">
              <div className="h-4 w-40 bg-emerald-100 animate-pulse rounded" />
              <div className="h-10 w-80 bg-slate-200 animate-pulse rounded-xl" />
            </div>
          </div>
          <div className="h-20 w-64 bg-white rounded-[32px] animate-pulse border border-slate-100" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-white rounded-[40px] animate-pulse border border-slate-100" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 h-[500px] bg-white rounded-[45px] animate-pulse border border-slate-100" />
          <div className="lg:col-span-4 space-y-8">
            <div className="h-72 bg-[#143E78]/10 rounded-[45px] animate-pulse" />
            <div className="h-64 bg-white rounded-[45px] animate-pulse border border-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  const { hospital, leadStats, surgeryStats, upcomingSurgeries } = data;
  const surgeriesWithOTP = upcomingSurgeries?.filter(s => s.activeOTP) || [];

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-8 lg:space-y-10 bg-[#F8FAFC] min-h-screen pb-20">

      {/* Active OTP Verifications Section */}
      {surgeriesWithOTP.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-[32px] sm:rounded-[40px] p-6 sm:p-8 lg:p-10 shadow-md animate-fadeIn">
          <h3 className="text-xl font-black text-amber-800 flex items-center gap-3 mb-6 sm:mb-8">
            <Zap className="w-6 h-6 text-amber-500" /> Action Required: Active Verifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {surgeriesWithOTP.map(surgery => (
              <div key={surgery._id} className="bg-white p-6 rounded-3xl shadow-sm border border-amber-100 flex flex-col gap-5">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-black text-[#143E78] text-lg truncate">{surgery.surgeryName}</h4>
                    <p className="text-xs font-bold text-gray-500 uppercase truncate mt-1">{surgery.lead?.patientName}</p>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-full whitespace-nowrap ml-3">
                    {surgery.activeOTP.purpose?.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col items-center justify-center">
                  <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Generated OTP Code</p>
                  <p className="text-3xl font-black tracking-[0.2em] text-gray-800">{surgery.activeOTP.code}</p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 mt-auto">
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="Enter OTP"
                    value={otpInputs[surgery._id] || ''}
                    onChange={(e) => setOtpInputs(prev => ({ ...prev, [surgery._id]: e.target.value.replace(/\D/g, '') }))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-center tracking-widest"
                  />
                  <button
                    onClick={() => handleVerifyOtp(surgery._id, surgery.activeOTP._id, surgery.activeOTP.purpose)}
                    disabled={verifyingOtp || (otpInputs[surgery._id]?.length !== 6)}
                    className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl text-sm font-black uppercase transition-colors shrink-0"
                  >
                    Verify
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Facility Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <div className="w-14 h-14 sm:w-20 sm:h-20 bg-emerald-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-lg shadow-emerald-900/20 flex-shrink-0">
            <Building2 className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Certified Health Partner</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-[#143E78] tracking-tight truncate">{hospital.hospitalName}</h1>
            <p className="text-gray-400 font-medium text-xs sm:text-base">Facility Management & Surgical Logistics Hub</p>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-2xl sm:rounded-[32px] border border-gray-100 shadow-sm flex items-center gap-4 w-full sm:w-fit">
          <div className="bg-emerald-50 p-2 sm:p-3 rounded-xl"><Zap className="w-5 h-5 text-emerald-600" /></div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Lifetime Revenue</p>
            <p className="text-xl font-black text-gray-800 tracking-tighter">₹{hospital.totalRevenue?.toLocaleString() || '0'}</p>
          </div>
        </div>
      </div>

      {/* High-Level Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
        {[
          { label: 'Total Admissions', value: hospital.totalPatients || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Procedures', value: hospital.totalSurgeries || 0, icon: Stethoscope, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Assigned Leads', value: data.assignedLeads || 0, icon: Heart, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Pending Payouts', value: data.payouts || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 sm:p-8 rounded-[2rem] sm:rounded-[40px] shadow-xl shadow-gray-200/40 border border-gray-100 group hover:scale-[1.02] transition-all">
            <div className={`w-10 h-10 sm:w-14 sm:h-14 ${stat.bg} ${stat.color} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6`}>
              <stat.icon className="w-5 h-5 sm:w-8 sm:h-8" />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{stat.label}</p>
            <h4 className="text-2xl sm:text-4xl font-black text-[#143E78] tracking-tighter">{stat.value}</h4>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Theater Schedule */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-[2rem] sm:rounded-[45px] p-6 sm:p-10 shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-10 gap-4">
              <h3 className="text-xl sm:text-2xl font-black text-[#143E78] flex items-center gap-3">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" /> Operational Calendar
              </h3>
              <Link to="/surgeries" className="px-5 py-2 bg-emerald-50 text-emerald-700 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 w-fit">Live Feed</Link>
            </div>

            <div className="space-y-4">
              {upcomingSurgeries.length > 0 ? upcomingSurgeries.map(surgery => (
                <div key={surgery._id} className="p-4 sm:p-6 bg-gray-50/50 rounded-2xl sm:rounded-[32px] border border-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between group hover:bg-white hover:border-emerald-100 transition-all gap-4">
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-[8px] sm:text-[10px] font-black text-emerald-600 uppercase italic leading-none mb-1">{new Date(surgery.scheduledDate).toLocaleDateString('default', { weekday: 'short' })}</span>
                      <span className="text-xl sm:text-2xl font-black text-[#143E78] leading-none">{new Date(surgery.scheduledDate).getDate()}</span>
                    </div>
                    <div>
                      <h4 className="text-base sm:text-lg font-black text-gray-800 uppercase tracking-tighter leading-tight">{surgery.surgeryName}</h4>
                      <p className="text-[10px] sm:text-xs text-gray-400 font-bold uppercase tracking-widest mt-0.5">{surgery.lead?.patientName || 'Patient'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-6 sm:gap-8 border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-100">
                    <div className="text-left sm:text-right">
                      <p className="text-xs sm:text-sm font-black text-[#143E78] italic uppercase tracking-tighter">{surgery.scheduledTime}</p>
                      <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-widest">Theatre A1</p>
                    </div>
                    <Link to={`/surgeries/${surgery._id}`} className="bg-[#143E78] text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg shadow-blue-900/30 group-hover:scale-110 transition-transform">
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Link>
                  </div>
                </div>
              )) : (
                <div className="py-20 text-center font-bold text-gray-300">Surgery schedule is currently empty.</div>
              )}
            </div>

            {/* Aesthetic Background */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-16 -mt-16 -z-0"></div>
          </div>
        </div>

        {/* Facility Widgets */}
        <div className="lg:col-span-4 space-y-8">

          {/* Patient Flow Card */}
          <div className="bg-white rounded-[2rem] sm:rounded-[45px] p-8 sm:p-10 border border-gray-100 shadow-xl shadow-gray-200/50">
            <h3 className="text-xl font-black text-[#143E78] mb-6">Patient Flow</h3>
            <div className="space-y-6">
              {[
                { label: 'Confirmed', val: leadStats?.confirmed || 0, color: 'text-emerald-500' },
                { label: 'Screened', val: leadStats?.screened || 0, color: 'text-blue-500' },
                { label: 'Follow-ups', val: leadStats?.contacted || 0, color: 'text-amber-500' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.label}</span>
                  <span className={`text-xl font-black ${item.color}`}>{item.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Doxez Facility Operations Support */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] sm:rounded-[45px] p-8 text-white border border-slate-700 shadow-xl relative overflow-hidden group">
            <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-blue-500/10 rounded-l-full blur-2xl pointer-events-none group-hover:bg-blue-500/20 transition-all duration-300"></div>
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-blue-500/10 p-2 rounded-xl border border-blue-500/20"><Building2 className="w-5 h-5 text-blue-400" /></div>
                <h3 className="text-sm font-black uppercase tracking-wider text-blue-200">Doxez Support Desk</h3>
              </div>
              <p className="text-[11px] font-bold text-slate-300 leading-relaxed">
                Our support team is available 24/7 to help you with surgery scheduling and payments.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`Hello Doxez Support Desk, I am representing ${hospital?.hospitalName || 'hospital partner'}. We need assistance.`);
                    window.open(`https://wa.me/917735970021?text=${text}`, '_blank');
                  }}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-wider text-center transition-all"
                >
                  WhatsApp Chat
                </button>
                <Link
                  to="/support"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-wider text-center transition-all"
                >
                  Support Hub
                </Link>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};

export default HospitalDashboard;